import { useState, type FormEvent } from 'react';
import { Paperclip, FileCheck2, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/authStore';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export function SancionModal({
  open, proveedorID, onClose, onCreated,
}: {
  open: boolean;
  proveedorID: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [tipo, setTipo] = useState('inhabilitacion');
  const [autoridad, setAutoridad] = useState('');
  const [oficio, setOficio] = useState('');
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().slice(0, 10));
  const [fechaFin, setFechaFin] = useState('');
  const [motivo, setMotivo] = useState('');
  const [importeMulta, setImporteMulta] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const token = useAuth((s) => s.token);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      let documento_id: string | undefined;
      if (archivo) {
        // Subir oficio/carta al módulo documentos, luego referenciar el id
        const fd = new FormData();
        fd.append('file', archivo);
        fd.append('modulo_origen', 'proveedores');
        fd.append('entidad', 'proveedor_sancion');
        fd.append('entidad_id', proveedorID);
        fd.append('tipo', 'oficio_sancion');

        const res = await fetch('/api/documentos', {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        const body = await res.json();
        if (!res.ok) {
          if (res.status === 422 && body.clamav_estado === 'infectado') {
            throw new Error('El archivo fue detectado como malicioso por ClamAV y puesto en cuarentena.');
          }
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        documento_id = body.id;
      }

      await api(`/proveedores/${proveedorID}/sanciones`, {
        method: 'POST',
        body: {
          tipo, autoridad, oficio, fecha_inicio: fechaInicio,
          fecha_fin: fechaFin || undefined,
          motivo,
          importe_multa: importeMulta ? parseFloat(importeMulta) : undefined,
          documento_id,
        },
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Registrar sanción"
      subtitle="La sanción quedará en el expediente del proveedor y en la bitácora de auditoría. Puedes adjuntar el oficio/carta de notificación."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">Cancelar</Button>
          <Button type="submit" form="sancion-form" loading={submitting} variant="danger">Registrar</Button>
        </>
      }
    >
      <form id="sancion-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">{err}</div>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="text-[13px] font-medium text-ink">Tipo</label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle"
            >
              <option value="inhabilitacion">Inhabilitación</option>
              <option value="suspension">Suspensión</option>
              <option value="multa">Multa</option>
              <option value="amonestacion">Amonestación</option>
              <option value="incumplimiento_contractual">Incumplimiento contractual</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          <Input name="autoridad" label="Autoridad" required placeholder="SFP, OIC, Contraloría municipal…"
                 value={autoridad} onChange={(e) => setAutoridad(e.target.value)} />
          <Input name="oficio" label="Oficio" placeholder="OM-2026-042"
                 value={oficio} onChange={(e) => setOficio(e.target.value)} />
          <Input name="importeMulta" label="Importe de multa" type="number" step="0.01"
                 value={importeMulta} onChange={(e) => setImporteMulta(e.target.value)} />
          <Input name="fechaInicio" label="Fecha inicio" type="date" required
                 value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
          <Input name="fechaFin" label="Fecha fin" type="date" hint="Vacío = indefinida"
                 value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
        </div>

        <div>
          <label className="text-[13px] font-medium text-ink">Motivo</label>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            required rows={3}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px] shadow-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-ink">
            Oficio o carta de notificación <span className="text-ink-soft">(opcional)</span>
          </label>
          <p className="mt-0.5 text-[12px] text-ink-mute">PDF, imagen o documento. Se analiza con ClamAV antes de adjuntarse.</p>
          {archivo ? (
            <div className="mt-2 flex items-center gap-3 rounded-md border border-border bg-canvas px-3 py-2.5">
              <FileCheck2 size={18} className="text-emerald-600" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-ink">{archivo.name}</div>
                <div className="text-[11px] text-ink-mute">
                  {(archivo.size / 1024).toFixed(1)} KB · {archivo.type || 'tipo desconocido'}
                </div>
              </div>
              <button type="button" onClick={() => setArchivo(null)}
                className="rounded-md p-1 text-ink-mute hover:bg-muted hover:text-danger">
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-canvas py-4 text-[13px] text-ink-mute transition-colors hover:border-brand-500 hover:text-ink">
              <Paperclip size={15} />
              Seleccionar archivo
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tif,.tiff"
                className="hidden"
                onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
              />
            </label>
          )}
        </div>
      </form>
    </Modal>
  );
}
